import { BoatState, WindState, GameState, Buoy, StartFinishLine, Vector2D } from '../types';
import { renderWater } from './waterRenderer';
import { renderBoat, renderWake } from './boatRenderer';
import { renderBuoy } from './buoyRenderer';
import { renderWindIndicator } from './windIndicator';
import { CoastLine, renderCoast } from './coastRenderer';

export interface Camera {
  x: number;
  y: number;
}

export function renderStartFinishLine(
  ctx: CanvasRenderingContext2D,
  line: StartFinishLine,
  isActive: boolean,
  camera: Camera
) {
  const startX = line.start.x - camera.x;
  const startY = line.start.y - camera.y;
  const endX = line.end.x - camera.x;
  const endY = line.end.y - camera.y;

  ctx.save();

  // Draw the line
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.strokeStyle = isActive ? '#ffcc00' : 'rgba(255, 255, 255, 0.6)';
  ctx.lineWidth = isActive ? 4 : 2;
  ctx.setLineDash(isActive ? [] : [10, 5]);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw end markers
  const markerSize = 8;

  // Start marker (green)
  ctx.beginPath();
  ctx.arc(startX, startY, markerSize, 0, Math.PI * 2);
  ctx.fillStyle = '#22c55e';
  ctx.fill();
  ctx.strokeStyle = '#166534';
  ctx.lineWidth = 2;
  ctx.stroke();

  // End marker (red)
  ctx.beginPath();
  ctx.arc(endX, endY, markerSize, 0, Math.PI * 2);
  ctx.fillStyle = '#ef4444';
  ctx.fill();
  ctx.strokeStyle = '#991b1b';
  ctx.stroke();

  // Label
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  ctx.fillStyle = 'white';
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText('START / FINISH', midX, midY - 10);

  ctx.restore();
}

export function renderCoastWithCamera(
  ctx: CanvasRenderingContext2D,
  coast: CoastLine,
  camera: Camera,
  isDarkMode: boolean
) {
  ctx.save();

  // Translate by camera offset
  ctx.translate(-camera.x, -camera.y);

  const lakePoints = coast.lakePoints;
  const outerPoints = coast.outerPoints;

  // Draw the land mass (everything except the lake)
  // Use fill rule 'evenodd' to cut out the lake from the land
  ctx.beginPath();

  // Outer boundary (large rectangle of land)
  ctx.moveTo(outerPoints[0].x, outerPoints[0].y);
  for (let i = 1; i < outerPoints.length; i++) {
    ctx.lineTo(outerPoints[i].x, outerPoints[i].y);
  }
  ctx.closePath();

  // Lake shoreline (will be cut out) - draw with smooth curves
  ctx.moveTo(lakePoints[0].x, lakePoints[0].y);
  for (let i = 0; i < lakePoints.length; i++) {
    const current = lakePoints[i];
    const next = lakePoints[(i + 1) % lakePoints.length];
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    ctx.quadraticCurveTo(current.x, current.y, midX, midY);
  }
  ctx.closePath();

  // Land gradient - richer colors
  const gradient = ctx.createLinearGradient(
    coast.center.x - coast.radiusX,
    coast.center.y - coast.radiusY,
    coast.center.x + coast.radiusX,
    coast.center.y + coast.radiusY
  );
  if (isDarkMode) {
    gradient.addColorStop(0, '#2d4a3e');
    gradient.addColorStop(0.3, '#1e3a2f');
    gradient.addColorStop(0.7, '#1a332a');
    gradient.addColorStop(1, '#152a22');
  } else {
    gradient.addColorStop(0, '#8bc583');
    gradient.addColorStop(0.3, '#6db365');
    gradient.addColorStop(0.7, '#5a9c52');
    gradient.addColorStop(1, '#4a8c42');
  }

  ctx.fillStyle = gradient;
  ctx.fill('evenodd'); // This cuts out the lake from the land

  // Draw the shoreline with smooth curves
  ctx.beginPath();
  ctx.moveTo(lakePoints[0].x, lakePoints[0].y);
  for (let i = 0; i < lakePoints.length; i++) {
    const current = lakePoints[i];
    const next = lakePoints[(i + 1) % lakePoints.length];
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    ctx.quadraticCurveTo(current.x, current.y, midX, midY);
  }
  ctx.closePath();

  // Sandy beach/shore line - outer edge
  ctx.strokeStyle = isDarkMode ? '#a08060' : '#f5e6c8';
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Inner beach highlight
  ctx.strokeStyle = isDarkMode ? '#8b7050' : '#e8d4a8';
  ctx.lineWidth = 3;
  ctx.stroke();

  // Subtle water edge effect
  ctx.strokeStyle = isDarkMode ? 'rgba(100, 150, 180, 0.3)' : 'rgba(150, 200, 230, 0.4)';
  ctx.lineWidth = 8;
  ctx.stroke();

  ctx.restore();
}

// Render minimap in bottom-right corner
function renderMinimap(
  ctx: CanvasRenderingContext2D,
  coast: CoastLine,
  boat: BoatState,
  buoys: Buoy[],
  gameState: GameState,
  startFinishLine: StartFinishLine | null,
  canvasWidth: number,
  canvasHeight: number,
  isDarkMode: boolean
) {
  const mapSize = 140;
  const mapMargin = 15;
  const mapX = canvasWidth - mapSize - mapMargin;
  const mapY = canvasHeight - mapSize - mapMargin;

  ctx.save();

  // Calculate scale to fit lake in minimap
  const lakeWidth = coast.bounds.right - coast.bounds.left;
  const lakeHeight = coast.bounds.bottom - coast.bounds.top;
  const scale = Math.min(
    (mapSize - 20) / lakeWidth,
    (mapSize - 20) / lakeHeight
  );

  // Offset to center the lake in minimap
  const offsetX = mapX + mapSize / 2 - (coast.center.x * scale);
  const offsetY = mapY + mapSize / 2 - (coast.center.y * scale);

  // Draw minimap background
  ctx.fillStyle = isDarkMode ? 'rgba(20, 30, 40, 0.85)' : 'rgba(240, 245, 250, 0.85)';
  ctx.beginPath();
  ctx.roundRect(mapX - 5, mapY - 5, mapSize + 10, mapSize + 10, 8);
  ctx.fill();

  // Border
  ctx.strokeStyle = isDarkMode ? 'rgba(100, 120, 140, 0.6)' : 'rgba(100, 120, 140, 0.4)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Clip to minimap area
  ctx.beginPath();
  ctx.roundRect(mapX, mapY, mapSize, mapSize, 6);
  ctx.clip();

  // Draw land (fill entire minimap, then draw lake on top)
  ctx.fillStyle = isDarkMode ? '#2d4a3e' : '#6db365';
  ctx.fillRect(mapX, mapY, mapSize, mapSize);

  // Draw lake water
  ctx.beginPath();
  const firstPoint = coast.lakePoints[0];
  ctx.moveTo(firstPoint.x * scale + offsetX, firstPoint.y * scale + offsetY);
  for (let i = 1; i < coast.lakePoints.length; i++) {
    const p = coast.lakePoints[i];
    ctx.lineTo(p.x * scale + offsetX, p.y * scale + offsetY);
  }
  ctx.closePath();
  ctx.fillStyle = isDarkMode ? '#1a3a4a' : '#5cacee';
  ctx.fill();

  // Draw shoreline
  ctx.strokeStyle = isDarkMode ? '#a08060' : '#e8d4a8';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Draw start/finish line
  if (startFinishLine && gameState.mode === 'race') {
    ctx.beginPath();
    ctx.moveTo(
      startFinishLine.start.x * scale + offsetX,
      startFinishLine.start.y * scale + offsetY
    );
    ctx.lineTo(
      startFinishLine.end.x * scale + offsetX,
      startFinishLine.end.y * scale + offsetY
    );
    ctx.strokeStyle = '#ffcc00';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Draw buoys
  buoys.forEach((buoy, index) => {
    const bx = buoy.position.x * scale + offsetX;
    const by = buoy.position.y * scale + offsetY;
    const isRounded = gameState.buoysRounded.includes(index);
    const isNext = gameState.mode === 'race' && index === gameState.currentBuoyIndex;

    ctx.beginPath();
    ctx.arc(bx, by, isNext ? 4 : 3, 0, Math.PI * 2);
    ctx.fillStyle = isRounded ? '#888' : (isNext ? '#ffcc00' : '#ef4444');
    ctx.fill();
  });

  // Draw boat position and heading
  const boatX = boat.position.x * scale + offsetX;
  const boatY = boat.position.y * scale + offsetY;

  ctx.save();
  ctx.translate(boatX, boatY);
  ctx.rotate(boat.heading);

  // Boat triangle
  ctx.beginPath();
  ctx.moveTo(0, -6);
  ctx.lineTo(-4, 5);
  ctx.lineTo(4, 5);
  ctx.closePath();
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.strokeStyle = isDarkMode ? '#333' : '#666';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();

  ctx.restore();

  // "MINIMAP" label
  ctx.fillStyle = isDarkMode ? 'rgba(200, 210, 220, 0.6)' : 'rgba(80, 90, 100, 0.6)';
  ctx.font = '9px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('MINIMAP', mapX + mapSize / 2, mapY + mapSize + 12);
}

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  boat: BoatState,
  wind: WindState,
  buoys: Buoy[],
  gameState: GameState,
  canvasWidth: number,
  canvasHeight: number,
  isDarkMode: boolean,
  startFinishLine: StartFinishLine | null,
  scale: number = 1,
  coast: CoastLine | null = null,
  camera: Camera = { x: 0, y: 0 }
) {
  const timestamp = Date.now();

  // Render infinite water background
  renderWater(ctx, canvasWidth, canvasHeight, isDarkMode);

  // Render coast/land with camera offset
  if (coast) {
    renderCoastWithCamera(ctx, coast, camera, isDarkMode);
  }

  // Render start/finish line with camera offset
  if (startFinishLine && gameState.mode === 'race') {
    const isLineActive = gameState.raceStatus === 'racing' &&
      gameState.buoysRounded.length === buoys.length;
    renderStartFinishLine(ctx, startFinishLine, isLineActive, camera);
  }

  // Render buoys with camera offset
  buoys.forEach((buoy, index) => {
    const isNext =
      gameState.mode === 'race' && index === gameState.currentBuoyIndex;
    const isRounded = gameState.buoysRounded.includes(index);

    // Create a camera-adjusted buoy position
    const adjustedBuoy = {
      ...buoy,
      position: {
        x: buoy.position.x - camera.x,
        y: buoy.position.y - camera.y,
      },
    };
    renderBuoy(ctx, adjustedBuoy, isNext, isRounded, timestamp);
  });

  // Render boat at camera-adjusted position (center of screen)
  const boatScreenPos = {
    ...boat,
    position: {
      x: boat.position.x - camera.x,
      y: boat.position.y - camera.y,
    },
  };

  renderWake(ctx, boatScreenPos, scale);
  renderBoat(ctx, boatScreenPos, scale);

  // Wind indicator - fixed position on screen (top right)
  renderWindIndicator(
    ctx,
    wind,
    canvasWidth - 70,
    70,
    45,
    isDarkMode
  );

  // Minimap - fixed position on screen (bottom right)
  if (coast) {
    renderMinimap(
      ctx,
      coast,
      boat,
      buoys,
      gameState,
      startFinishLine,
      canvasWidth,
      canvasHeight,
      isDarkMode
    );
  }

  // UI overlays (not affected by camera)
  if (gameState.isPaused) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('PAUSED', canvasWidth / 2, canvasHeight / 2);

    ctx.font = '18px sans-serif';
    ctx.fillText(
      'Press P or Escape to resume',
      canvasWidth / 2,
      canvasHeight / 2 + 40
    );
  }

  if (gameState.raceStatus === 'countdown') {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 120px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      gameState.countdownValue.toString(),
      canvasWidth / 2,
      canvasHeight / 2
    );
  }

  if (gameState.raceStatus === 'finished') {
    ctx.fillStyle = 'rgba(0, 100, 0, 0.3)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('FINISHED!', canvasWidth / 2, canvasHeight / 2 - 30);

    const minutes = Math.floor(gameState.raceElapsedTime / 60000);
    const seconds = Math.floor((gameState.raceElapsedTime % 60000) / 1000);
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    ctx.font = 'bold 36px sans-serif';
    ctx.fillText(`Time: ${timeStr}`, canvasWidth / 2, canvasHeight / 2 + 30);
  }
}
